import { Audience, AudienceFilterOperator, AudienceFilterType } from '../../audience/components/audience.model';
import { SessionCondition, SessionConditionType } from '../../audience/components/session-condition-group.model';
import { AndOrOperator } from '../../campaign/widgets.model';
import { DataPlatformAudienceRule, DataPlatformBreakdownRow } from '../data-platform.model';

/**
 * Turns an audience's *un-countable* criteria (session conditions + a couple of
 * passthrough filters) into either:
 *  - `promotedRules`: criteria that map cleanly onto a countable DP rule
 *    (New/Returning visitor → `session_count`). These are folded into the base
 *    count so the audience stays **exact**.
 *  - `factors`: criteria we can only *estimate* by reading a backend
 *    distribution share (`getBreakdown`) and multiplying the base by it
 *    (device / country / source / utm-campaign).
 *  - `unquantifiable`: everything we have no backend signal for (JsExpression,
 *    Weather, Region, …); the caller keeps these as an honest "≤" upper bound.
 *
 * Pure module, mirrors the style of `audience-guesstimate.utils.ts`. The HTTP
 * fetch + orchestration lives in `DataPlatformAudienceReachService`.
 */

export type FactorDimension =
  'device_type' | 'geo_country' | 'session_source' | 'referrer_app' | 'utm_campaign_name' | 'os';

export type FactorMatchMode = 'exact' | 'startsWith' | 'endsWith' | 'contains';

export interface EstimateFactor {
  dimension: FactorDimension;
  /** Row labels whose share counts toward this factor. Multiple keys are summed
   *  (e.g. "social" = several referrer apps). Matched per `matchMode`. */
  bucketKeys: string[];
  /** How a row label is compared to a bucket key (case-insensitive). Defaults to
   *  exact equality; UTM-campaign conditions use startsWith/endsWith/contains. */
  matchMode?: FactorMatchMode;
  /** When true the criterion EXCLUDES the buckets → pass-rate is (1 - share). */
  negate?: boolean;
  /** Human label for the explanation note (e.g. "desktop", "SE", "email"). */
  label: string;
  /** Translation key for a generic label; values like "SE" or a campaign name have none. */
  labelKey?: string;
}

/** Shared one-line render of a factor note — keep hub & flyout identical. */
export function formatFactorNote(
  note: { share: number; label: string; labelKey?: string; negate?: boolean },
  translate: (key: string) => string,
): string {
  const label = note.labelKey ? translate(note.labelKey) : note.label;
  const prefix = note.negate ? translate('data-platform.reach-factor-label.not') + ' ' : '';
  return `≈${Math.round(note.share * 100)}% ${prefix}${label}`;
}

export interface MappedCriteria {
  /** Criteria counted exactly in the base, as rules. */
  promotedRules: DataPlatformAudienceRule[];
  /** Criteria estimated from a backend distribution share. */
  factors: EstimateFactor[];
  /** Criteria we have no backend signal for — keep as a "≤" bound. */
  unquantifiable: string[];
}

/** Device-type row synonyms — backend casing/wording isn't guaranteed. */
const DESKTOP_KEYS = ['desktop', 'computer'];
const MOBILE_KEYS = ['mobile', 'phone', 'smartphone'];
const TABLET_KEYS = ['tablet'];
/** The networks the onsite social media check matches. */
const SOCIAL_MEDIA_APP_KEYS = ['facebook', 'instagram', 'linkedin', 'twitter', 'pinterest'];

/** `os` rows carry a version ("iOS 26", "Mac OS X 10"), so they match by prefix. */
const OS_LABEL_PREFIXES: Readonly<Record<string, string>> = {
  Windows: 'windows',
  macOS: 'mac os x',
  iOS: 'ios',
  Android: 'android',
};

/** Every OS version is its own row, so the default top 20 would cut the tail off. */
const OS_TOP_N = 100;

export function factorTopN(dimension: FactorDimension): { topN?: number } {
  return dimension === 'os' ? { topN: OS_TOP_N } : {};
}

/**
 * Campaign session-condition → widget event-type. A "Campaign opened" condition
 * is the same signal as a `widget_event_count` rule on the `widget-open` event,
 * so we promote it to a countable rule for reach (the backend count endpoint).
 * Keep aligned with `audience-rule-editor`'s `widgetEventLabelKeys`.
 */
const CAMPAIGN_CONDITION_WIDGET_EVENT: Partial<Record<SessionConditionType, string>> = {
  [SessionConditionType.CampaignOpened]: 'widget-open',
  [SessionConditionType.CampaignClickthrough]: 'widget-clickthrough',
  [SessionConditionType.CampaignConversion]: 'widget-conversion',
  [SessionConditionType.CampaignDismissal]: 'widget-dismissal',
  [SessionConditionType.CampaignSurveyResponse]: 'widget-survey-response',
};

/** Resolves a campaign id to its widget id (the count endpoint keys on widgetId). */
export type CampaignWidgetResolver = (campaignId: number) => number | undefined;

export function campaignWidgetResolver(
  widgets: readonly { id?: number; campaignId?: number }[],
): CampaignWidgetResolver | undefined {
  if (!widgets.length) {
    return undefined;
  }
  const widgetIdByCampaign = new Map<number, number>();
  for (const widget of widgets) {
    if (widget.campaignId != null && widget.id != null) {
      widgetIdByCampaign.set(widget.campaignId, widget.id);
    }
  }
  return (campaignId: number) => widgetIdByCampaign.get(campaignId);
}

/**
 * Splits an audience's un-countable criteria into promoted rules, estimable
 * factors, and the leftover (unquantifiable) types.
 *
 * `resolveWidgetId` (optional) lets callers promote Campaign* widget-interaction
 * conditions into countable `widget_event_count` rules. It needs the widget list
 * (campaignId → widgetId), which the pure module can't load itself, so the hub
 * and flyout pass it in. Without it, Campaign* conditions stay unquantifiable
 * (an honest "≤" bound), exactly as before.
 */
export function mapCriteria(
  audience: Audience | null | undefined,
  resolveWidgetId?: CampaignWidgetResolver,
): MappedCriteria {
  const promotedRules: DataPlatformAudienceRule[] = [];
  const factors: EstimateFactor[] = [];
  const unquantifiable: string[] = [];
  if (!audience) return { promotedRules, factors, unquantifiable };

  for (const cond of audience.sessionConditions ?? []) {
    const negate = isNegationOperator(cond.operator) !== (cond.show === false);
    const widgetEventType = CAMPAIGN_CONDITION_WIDGET_EVENT[cond.type as SessionConditionType];
    if (widgetEventType) {
      const promoted = resolveWidgetId ? campaignConditionToWidgetRule(cond, widgetEventType, resolveWidgetId) : null;
      if (promoted) promotedRules.push(promoted);
      else unquantifiable.push(cond.type ?? 'unknown');
      continue;
    }
    switch (cond.type) {
      case SessionConditionType.OnlyDesktop:
        factors.push({
          dimension: 'device_type',
          bucketKeys: DESKTOP_KEYS,
          negate,
          label: 'desktop',
          labelKey: 'data-platform.reach-factor-label.desktop',
        });
        break;
      case SessionConditionType.OnlyMobile:
        factors.push({
          dimension: 'device_type',
          bucketKeys: MOBILE_KEYS,
          negate,
          label: 'mobile',
          labelKey: 'data-platform.reach-factor-label.mobile',
        });
        break;
      case SessionConditionType.Tablet:
        factors.push({
          dimension: 'device_type',
          bucketKeys: TABLET_KEYS,
          negate,
          label: 'tablet',
          labelKey: 'data-platform.reach-factor-label.tablet',
        });
        break;
      case SessionConditionType.MobileOrTablet:
        factors.push({
          dimension: 'device_type',
          bucketKeys: [...MOBILE_KEYS, ...TABLET_KEYS],
          negate,
          label: 'mobile or tablet',
          labelKey: 'data-platform.reach-factor-label.mobile-or-tablet',
        });
        break;
      case SessionConditionType.MarketingEmailVisit:
        factors.push({
          dimension: 'session_source',
          bucketKeys: ['email'],
          negate,
          label: 'email',
          labelKey: 'data-platform.reach-factor-label.email',
        });
        break;
      case SessionConditionType.FacebookVisit:
        factors.push({ dimension: 'referrer_app', bucketKeys: ['facebook'], negate, label: 'Facebook' });
        break;
      case SessionConditionType.InstagramVisit:
        factors.push({ dimension: 'referrer_app', bucketKeys: ['instagram'], negate, label: 'Instagram' });
        break;
      case SessionConditionType.OtherSocialMediaVisit:
        factors.push({
          dimension: 'referrer_app',
          bucketKeys: SOCIAL_MEDIA_APP_KEYS,
          negate,
          label: 'social',
          labelKey: 'data-platform.reach-factor-label.social',
        });
        break;
      case SessionConditionType.UTMCampaign: {
        const campaigns = splitValues(cond.value);
        if (campaigns.length) {
          // Honor the operator: StartsWith/EndsWith/Contains must match partial
          // campaign names against the breakdown rows, not exact-equal (which
          // would silently collapse the estimate to 0 for "spring_24_promo").
          factors.push({
            dimension: 'utm_campaign_name',
            bucketKeys: campaigns,
            matchMode: operatorMatchMode(cond.operator),
            negate,
            label: campaigns.join(', '),
          });
        } else {
          unquantifiable.push(cond.type);
        }
        break;
      }
      case SessionConditionType.OperatingSystem: {
        const systems = splitValues(cond.value).filter((value) => OS_LABEL_PREFIXES[value]);
        if (systems.length) {
          factors.push({
            dimension: 'os',
            bucketKeys: systems.map((value) => OS_LABEL_PREFIXES[value]),
            matchMode: 'startsWith',
            negate,
            label: systems.join(', '),
          });
        } else {
          unquantifiable.push(cond.type);
        }
        break;
      }
      case SessionConditionType.NewVisitor:
        promotedRules.push(
          negate
            ? { type: 'session_count', operator: 'gte', value: 2 }
            : { type: 'session_count', operator: 'eq', value: 1 },
        );
        break;
      case SessionConditionType.ReturningVisitor:
        promotedRules.push(...returningVisitorRules(cond));
        break;
      default:
        unquantifiable.push(cond.type ?? 'unknown');
    }
  }

  for (const filter of audience.filters ?? []) {
    // Region and Weather have no breakdown; NPS, CSAT and interest profiles count as rules.
    if (filter.type === AudienceFilterType.Country) {
      const codes = splitValues(filter.value);
      if (codes.length) {
        factors.push({
          dimension: 'geo_country',
          bucketKeys: codes,
          negate: isNegationOperator(filter.operator),
          label: codes.join(', '),
        });
      } else {
        unquantifiable.push('Country');
      }
    } else if (
      filter.type === AudienceFilterType.Region ||
      filter.type === AudienceFilterType.Weather ||
      filter.type === AudienceFilterType.Temperature
    ) {
      unquantifiable.push(filter.type);
    }
    // AppList/AppSegment/AppLabels/AppTags are the audience's 3rd-party *source*
    // (sized via the integration list total L), not a narrowing factor — ignore.
  }

  return { promotedRules, factors, unquantifiable };
}

export function matchesEitherSide(audience: Audience): boolean {
  return audience.operator === AndOrOperator.Or && !!audience.filters?.length && !!audience.sessionConditions?.length;
}

/**
 * Share of a distribution that passes a factor. Returns NaN when the breakdown
 * carried no usable data (empty rows / zero total) so the caller can degrade the
 * factor to "unquantifiable" rather than zeroing the reach. A present
 * distribution with the bucket absent legitimately yields 0.
 */
export function passRate(rows: DataPlatformBreakdownRow[] | null | undefined, factor: EstimateFactor): number {
  if (!rows || rows.length === 0) return NaN;
  const total = rows.reduce((sum, r) => sum + (r.value > 0 ? r.value : 0), 0);
  if (total <= 0) return NaN;
  const matched = rows
    .filter((r) => matchesBucket(r.label, factor))
    .reduce((sum, r) => sum + (r.value > 0 ? r.value : 0), 0);
  const share = matched / total;
  return factor.negate ? 1 - share : share;
}

/**
 * Absolute count of the matched buckets (the distribution's own value, not a
 * ratio) — used as the base population for an audience whose ONLY criterion is
 * an estimable condition (no countable rules to count). e.g. "Only mobile" →
 * the `mobile` bucket value. Honors negation (negate → total - matched).
 *
 * Returns NaN ONLY when no distribution was fetched (`null` — a failed call).
 * A successfully-fetched but empty distribution means the dimension genuinely
 * had no traffic (e.g. no in-app Facebook visits) → 0, a real answer, not "can't
 * calculate". An absent bucket within a non-empty distribution is likewise 0.
 */
export function matchedCount(rows: DataPlatformBreakdownRow[] | null | undefined, factor: EstimateFactor): number {
  if (rows == null) return NaN;
  const total = rows.reduce((sum, r) => sum + (r.value > 0 ? r.value : 0), 0);
  if (total <= 0) return 0;
  const matched = rows
    .filter((r) => matchesBucket(r.label, factor))
    .reduce((sum, r) => sum + (r.value > 0 ? r.value : 0), 0);
  return factor.negate ? total - matched : matched;
}

/**
 * base × Π(rates), clamped to [0, base]. NaN rates (no data) are dropped so they
 * don't collapse the estimate. Empty/all-NaN rates → base unchanged.
 */
export function applyFactors(base: number, rates: number[]): number {
  const b = Math.max(0, Math.round(base) || 0);
  if (b === 0) return 0;
  return Math.max(0, Math.min(b, Math.round(b * combinedShare(rates))));
}

export function combinedShare(rates: number[]): number {
  return rates
    .filter((rate) => Number.isFinite(rate))
    .reduce((product, rate) => product * Math.max(0, Math.min(1, rate)), 1);
}

// ---- internals ----

/**
 * Promotes a Campaign* widget-interaction condition into a countable
 * `widget_event_count` rule. Returns null when the campaign can't be resolved to
 * a widget id (e.g. the widget was deleted) so the caller keeps it as a "≤" bound.
 */
function campaignConditionToWidgetRule(
  cond: SessionCondition,
  widgetEventType: string,
  resolveWidgetId: CampaignWidgetResolver,
): DataPlatformAudienceRule | null {
  const parsed = parseCampaignConditionValue(cond.value);
  if (!parsed) return null;
  const widgetId = resolveWidgetId(parsed.campaignId);
  if (widgetId == null) return null;
  return {
    type: 'widget_event_count',
    widgetId,
    widgetEventType,
    operator: campaignOperatorToDpOp(cond.operator),
    value: parsed.times,
  };
}

/**
 * Parses a Campaign* condition's `value`: either a bare campaign id (→ times 1)
 * or a JSON `{ campaignId, times }`. Mirrors
 * `SessionConditionHelpers.parseCampaignEventConditionValue` without pulling the
 * helper's deps into this pure module.
 */
function parseCampaignConditionValue(value: string | null | undefined): { campaignId: number; times: number } | null {
  if (value == null || value === '') return null;
  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) return { campaignId: asNumber, times: 1 };
  try {
    const parsed = JSON.parse(value) as { campaignId?: number; times?: number };
    const campaignId = Number(parsed?.campaignId);
    if (!Number.isFinite(campaignId)) return null;
    const times = Number(parsed?.times);
    return { campaignId, times: Number.isFinite(times) && times > 0 ? times : 1 };
  } catch {
    return null;
  }
}

/** As in the widget script, any operator other than LessThan means more than. */
function returningVisitorRules(cond: SessionCondition): DataPlatformAudienceRule[] {
  const count = Number(cond.value);
  if (!cond.value || !Number.isFinite(count)) {
    return [{ type: 'session_count', operator: 'gte', value: 2 }];
  }
  if (cond.operator === AudienceFilterOperator.LessThan) {
    return [
      { type: 'session_count', operator: 'gte', value: 2 },
      { type: 'session_count', operator: 'lt', value: count },
    ];
  }
  return [{ type: 'session_count', operator: 'gt', value: count }];
}

function campaignOperatorToDpOp(op?: AudienceFilterOperator): string {
  switch (op) {
    case AudienceFilterOperator.Equals:
      return 'eq';
    case AudienceFilterOperator.MoreThan:
    case AudienceFilterOperator.GreaterThan:
      return 'gt';
    case AudienceFilterOperator.LessThan:
      return 'lt';
    default:
      return 'gte';
  }
}

/** Whether a breakdown row label matches any of the factor's buckets, per matchMode. */
function matchesBucket(label: string | undefined, factor: EstimateFactor): boolean {
  const l = (label ?? '').trim().toLowerCase();
  return factor.bucketKeys.some((rawKey) => {
    const key = rawKey.trim().toLowerCase();
    if (!key) return false;
    switch (factor.matchMode) {
      case 'startsWith':
        return l.startsWith(key);
      case 'endsWith':
        return l.endsWith(key);
      case 'contains':
        return l.includes(key);
      default:
        return l === key;
    }
  });
}

/** Maps an audience-filter operator to the bucket match mode for value-bearing factors. */
function operatorMatchMode(op?: AudienceFilterOperator): FactorMatchMode {
  switch (op) {
    case AudienceFilterOperator.StartsWith:
      return 'startsWith';
    case AudienceFilterOperator.EndsWith:
      return 'endsWith';
    case AudienceFilterOperator.Contains:
    case AudienceFilterOperator.NotContains:
      return 'contains';
    default:
      return 'exact';
  }
}

export function isNegationOperator(op?: AudienceFilterOperator): boolean {
  return (
    op === AudienceFilterOperator.NotEquals ||
    op === AudienceFilterOperator.NotAnyOf ||
    op === AudienceFilterOperator.NotContains ||
    op === AudienceFilterOperator.NotPartOf ||
    op === AudienceFilterOperator.NotHaveTag ||
    op === AudienceFilterOperator.DoNotHave ||
    op === AudienceFilterOperator.HaveNot
  );
}

function splitValues(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
